/**
 * Persist cashout-per-market locks across reloads. Scoped to login-session so
 * a new session does not inherit another user's (or stale) locks.
 *
 * Flow: place normal bet → unlock (show Cashout); place cashout hedge → lock (hide)
 * until the next normal bet on that market.
 *
 * Same behavior as lotusbet.cc (display-only gate — placebet math unchanged).
 */
const STORAGE_KEY = 'bet-cashout-locked-markets-v1'
export const CASHOUT_LOCK_CHANGED_EVENT = 'lotus-cashout-lock-changed'

type CashoutLockStore = {
  sessionId: string
  locks: Record<string, true>
}

function getSessionId(): string {
  if (typeof window === 'undefined') return ''
  try {
    return localStorage.getItem('login-session') || ''
  } catch {
    return ''
  }
}

function notifyCashoutLockChanged(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(CASHOUT_LOCK_CHANGED_EVENT))
}

export function loadPersistedCashoutLockKeys(): Record<string, true> {
  if (typeof window === 'undefined') return {}
  try {
    const sessionId = getSessionId()
    if (!sessionId) return {}
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as CashoutLockStore
    if (!parsed || parsed.sessionId !== sessionId) return {}
    return parsed.locks && typeof parsed.locks === 'object' ? { ...parsed.locks } : {}
  } catch {
    return {}
  }
}

export function persistCashoutLockKeys(locks: Record<string, true>): void {
  if (typeof window === 'undefined') return
  try {
    const sessionId = getSessionId()
    if (!sessionId) {
      localStorage.removeItem(STORAGE_KEY)
      return
    }
    if (Object.keys(locks).length === 0) {
      localStorage.removeItem(STORAGE_KEY)
      return
    }
    const payload: CashoutLockStore = { sessionId, locks: { ...locks } }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // QuotaExceededError, private mode, etc.
  }
}

export function isCashoutMarketLocked(marketId: string | number | undefined | null): boolean {
  if (marketId == null || marketId === '') return false
  const locks = loadPersistedCashoutLockKeys()
  return !!locks[String(marketId)]
}

/** After a successful cashout place-bet — hide Cashout until next normal bet. */
export function lockCashoutMarket(marketId: string | number | undefined | null): void {
  if (marketId == null || marketId === '') return
  const key = String(marketId)
  const locks = loadPersistedCashoutLockKeys()
  if (locks[key]) return
  locks[key] = true
  persistCashoutLockKeys(locks)
  notifyCashoutLockChanged()
}

/** After a successful normal place-bet — allow Cashout again for this market. */
export function unlockCashoutMarket(marketId: string | number | undefined | null): void {
  if (marketId == null || marketId === '') return
  const key = String(marketId)
  const locks = loadPersistedCashoutLockKeys()
  if (!locks[key]) return
  delete locks[key]
  persistCashoutLockKeys(locks)
  notifyCashoutLockChanged()
}

/** Call on logout so stale locks are not associated with the next session. */
export function clearPersistedCashoutLockKeys(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(STORAGE_KEY)
    notifyCashoutLockChanged()
  } catch {
    /* ignore */
  }
}
